import type { DateRange } from 'reka-ui';
import { parseDate } from '@internationalized/date';

interface CalendarStateOptions {
  model: Ref<DateRange | undefined>;
  isDesktop: Ref<boolean>;
  oneWay: Ref<boolean>;
  onRangeSelected: () => void;
}

export const useCalendarState = ({ model, isDesktop, oneWay, onRangeSelected }: CalendarStateOptions) => {
  const isOpen = ref(false);
  const mode = ref<'start' | 'end'>('start');
  const startSelectionInProgress = ref(false);
  const endSelectionInProgress = ref(false);

  const isStartOpen = computed(() => isOpen.value && mode.value === 'start');
  const isEndOpen = computed(() => isOpen.value && mode.value === 'end');

  // Idempotent counterpart of openStart, for the parent's autoOpen trigger: a toggle there could close
  // the calendar — or leave it on the end pane — instead of opening the start one.
  const ensureStartOpen = () => {
    mode.value = 'start';
    isOpen.value = true;
  };

  const openStart = () => {
    if (isStartOpen.value) {
      isOpen.value = false;
    } else {
      ensureStartOpen();
    }
  };

  const openEnd = () => {
    if (isEndOpen.value) {
      isOpen.value = false;
    } else {
      mode.value = 'end';
      isOpen.value = true;
    }
  };

  const onStartCalendarMouseenter = (event: MouseEvent) => {
    if (!model.value?.start || !model.value?.end) return;
    const target = event.target;
    const cell = target instanceof Element ? target.closest('td') : undefined;
    if (!cell) {
      startSelectionInProgress.value = false;
      return;
    }
    const dateString = cell.querySelector<HTMLElement>('[data-date]')?.dataset.date;
    if (!dateString) {
      startSelectionInProgress.value = true;
      return;
    }
    startSelectionInProgress.value = parseDate(dateString).compare(model.value.end) <= 0;
  };

  const onStartCalendarMouseleave = () => {
    startSelectionInProgress.value = false;
  };

  const onEndCalendarMouseenter = (event: MouseEvent) => {
    if (!model.value?.start || !model.value?.end) return;
    const target = event.target;
    endSelectionInProgress.value = target instanceof Element && !!target.closest('td');
  };

  const onEndCalendarMouseleave = () => {
    endSelectionInProgress.value = false;
  };

  let rangeSelectedPending = false;

  const { start: scheduleClose } = useTimeoutFn(
    () => {
      isOpen.value = false;
      if (rangeSelectedPending) {
        rangeSelectedPending = false;
        onRangeSelected();
      }
    },
    400,
    { immediate: false },
  );

  const { start: scheduleEndMode } = useTimeoutFn(
    () => {
      if (isOpen.value && mode.value === 'start') mode.value = 'end';
    },
    200,
    { immediate: false },
  );

  watch(model, (value) => {
    if (isDesktop.value && mode.value === 'start' && (oneWay.value ? value?.start : value?.start && value?.end))
      isOpen.value = false;
  });

  watch(
    () => model.value?.start,
    () => {
      if (mode.value !== 'start' || !isOpen.value) return;
      if (oneWay.value) {
        scheduleClose();
      } else if (!isDesktop.value) {
        if (model.value?.end) {
          scheduleClose();
        } else {
          mode.value = 'end';
        }
      } else if (!model.value?.end) {
        scheduleEndMode();
      }
    },
  );

  watch(isStartOpen, (open) => {
    if (!open) startSelectionInProgress.value = false;
  });
  watch(isEndOpen, (open) => {
    if (!open) endSelectionInProgress.value = false;
  });

  // Tracks whether the range was still incomplete when the sheet/popover opened, so `onRangeSelected` fires
  // for a fresh guided selection — not when the user reopens an already-filled range to edit it.
  const rangeEmptyOnOpen = ref(false);

  watch(isOpen, (open) => {
    if (open) {
      rangeEmptyOnOpen.value = oneWay.value ? !model.value?.start : !model.value?.start || !model.value?.end;
    }
  });

  // Watch the model, not `oneWay ? start : end`: a trip-type toggle would re-evaluate that source and
  // fire as if a date had just been picked.
  watch(model, () => {
    const value = oneWay.value ? model.value?.start : model.value?.end;
    if (!value || !model.value?.start || !rangeEmptyOnOpen.value) {
      return;
    }
    rangeEmptyOnOpen.value = false;
    // Desktop closes in the same flush; mobile goes through the deferred close, so the next sheet
    // does not open on top of the calendar still on screen.
    if (isDesktop.value) {
      onRangeSelected();
    } else {
      rangeSelectedPending = true;
    }
  });

  return {
    isOpen,
    mode,
    isStartOpen,
    isEndOpen,
    startSelectionInProgress,
    endSelectionInProgress,
    ensureStartOpen,
    openStart,
    openEnd,
    scheduleClose,
    onStartCalendarMouseenter,
    onStartCalendarMouseleave,
    onEndCalendarMouseenter,
    onEndCalendarMouseleave,
  };
};
